import { ComponentSize, ElMessage } from 'element-plus';
import { defineStore } from 'pinia';
import { store } from '../index';

type ThemeType = 'light' | 'dark' | 'auto';

interface AppState {
  breadcrumb: boolean;
  collapse: boolean;
  uniqueOpened: boolean;
  screenfull: boolean;
  size: boolean;
  locale: boolean;
  tagsView: boolean;
  logo: boolean;
  fixedHeader: boolean;
  greyMode: boolean;
  pageLoading: boolean;
  layout: LayoutType;
  title: string;
  currentSize: ComponentSize;
  sizeMap: ComponentSize[];
  mobile: boolean;
  footer: boolean;
  fixedMenu: boolean;
  mobileMenuDrawer: boolean;
  theme: ThemeType;
}

export const useAppStore = defineStore('app', {
  state: (): AppState => {
    return {
      mobileMenuDrawer: false,
      sizeMap: ['default', 'large', 'small'],
      mobile: false, // 是否是移动端
      title: import.meta.env.VITE_APP_TITLE, // 标题
      pageLoading: false, // 路由跳转loading
      breadcrumb: true, // 面包屑
      collapse: false, // 折叠菜单
      uniqueOpened: false, // 是否只保持一个子菜单的展开
      screenfull: true, // 全屏图标
      size: true, // 尺寸图标
      locale: true, // 多语言图标
      tagsView: true, // 标签页
      logo: true, // logo
      fixedHeader: true, // 固定toolheader
      footer: true, // 显示页脚
      greyMode: false, // 是否开始灰色模式，用于特殊悼念日
      fixedMenu: false, // 是否固定菜单

      layout: 'classic', // layout布局
      currentSize: 'default', // 组件尺寸
      theme: 'light', // 主题模式
    };
  },
  getters: {
    getBreadcrumb(): boolean {
      return this.breadcrumb;
    },
    getCollapse(): boolean {
      return this.collapse;
    },
    getUniqueOpened(): boolean {
      return this.uniqueOpened;
    },
    getScreenfull(): boolean {
      return this.screenfull;
    },
    getSize(): boolean {
      return this.size;
    },
    getLocale(): boolean {
      return this.locale;
    },
    getTagsView(): boolean {
      return this.tagsView;
    },
    getLogo(): boolean {
      return this.logo;
    },
    getFixedHeader(): boolean {
      return this.fixedHeader;
    },
    getGreyMode(): boolean {
      return this.greyMode;
    },
    getFixedMenu(): boolean {
      return this.fixedMenu;
    },
    getPageLoading(): boolean {
      return this.pageLoading;
    },
    getLayout(): LayoutType {
      return this.layout;
    },
    getTitle(): string {
      return this.title;
    },
    getCurrentSize(): ComponentSize {
      return this.currentSize;
    },
    getSizeMap(): ComponentSize[] {
      return this.sizeMap;
    },
    getMobile(): boolean {
      return this.mobile;
    },
    getFooter(): boolean {
      return this.footer;
    },
    getTheme(): ThemeType {
      return this.theme;
    },
  },
  actions: {
    setBreadcrumb(breadcrumb: boolean) {
      this.breadcrumb = breadcrumb;
    },
    setCollapse(collapse: boolean) {
      this.collapse = collapse;
    },
    setUniqueOpened(uniqueOpened: boolean) {
      this.uniqueOpened = uniqueOpened;
    },
    setScreenfull(screenfull: boolean) {
      this.screenfull = screenfull;
    },
    setSize(size: boolean) {
      this.size = size;
    },
    setLocale(locale: boolean) {
      this.locale = locale;
    },
    setTagsView(tagsView: boolean) {
      this.tagsView = tagsView;
    },
    setLogo(logo: boolean) {
      this.logo = logo;
    },
    setFixedHeader(fixedHeader: boolean) {
      this.fixedHeader = fixedHeader;
    },
    setGreyMode(greyMode: boolean) {
      this.greyMode = greyMode;
    },
    setFixedMenu(fixedMenu: boolean) {
      this.fixedMenu = fixedMenu;
    },
    setPageLoading(pageLoading: boolean) {
      this.pageLoading = pageLoading;
    },
    setLayout(layout: LayoutType) {
      if (this.mobile && layout !== 'classic') {
        ElMessage.warning('移动端模式下不支持切换其它布局');
        return;
      }
      this.layout = layout;
    },
    setTitle(title: string) {
      this.title = title;
    },
    setCurrentSize(currentSize: ComponentSize) {
      this.currentSize = currentSize;
    },
    setMobile(mobile: boolean) {
      this.mobile = mobile;
    },
    setFooter(footer: boolean) {
      this.footer = footer;
    },
    setMobileMenuDrawer(mobileMenuDrawer: boolean) {
      this.mobileMenuDrawer = mobileMenuDrawer;
    },
    setTheme(theme: ThemeType) {
      this.theme = theme;
      this.applyTheme();
    },
    applyTheme(routeMeta?: any) {
      const rootElement = document.documentElement;
      // 检查当前路由是否强制暗黑主题
      const forceDarkTheme = routeMeta?.forceDarkTheme;
      
      // 如果路由配置了强制暗黑主题，则使用暗黑模式，否则按照用户设置的主题
      const isDark = forceDarkTheme || this.theme === 'dark' || (this.theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      
      if (isDark) {
        // 应用暗黑主题
        rootElement.style.setProperty('--app-content-bg-color', '#181818');
        rootElement.style.setProperty('--left-menu-bg-color', '#202020');
        rootElement.style.setProperty('--top-header-bg-color', '#202020');
        rootElement.style.setProperty('--top-header-text-color', '#f1f5f9');
        rootElement.style.setProperty('--top-header-hover-color', '#334155');
        rootElement.classList.add('dark');
        document.documentElement.classList.add('dark');
        document.body.classList.add('dark');
        
        // 强制更新body背景色
        document.body.style.backgroundColor = '#181818';
      } else {
        // 应用明亮主题
        rootElement.style.setProperty('--app-content-bg-color', '#FDFCFC');
        rootElement.style.setProperty('--left-menu-bg-color', '#001529');
        rootElement.style.setProperty('--top-header-bg-color', '#fff');
        rootElement.style.setProperty('--top-header-text-color', 'inherit');
        rootElement.style.setProperty('--top-header-hover-color', '#f6f6f6');
        rootElement.classList.remove('dark');
        document.documentElement.classList.remove('dark');
        document.body.classList.remove('dark');
        
        // 强制更新body背景色
        document.body.style.backgroundColor = '#F1F7FE';
      }
    },

  },
  persist: true,
});

export const useAppStoreWithOut = () => {
  return useAppStore(store);
};
